// New-lead "Tasks due" — backend-capability compatibility.
//
// The `new-lead-task-widget-counts` endpoint is being rolled out in two shapes:
//   NEW backend: returns a `due_now` key; `overdue` and `due_today` are DISJOINT;
//                `task_due_bucket=due_now` is understood by /leads/.
//   OLD backend: no `due_now` key; `due_today` is a SUPERSET of `overdue` (it already
//                includes the overdue leads); `task_due_bucket=due_now` is silently
//                ignored by django-filter.
// This one module encodes the rule so the table strip (NewLeadTaskDueWidget), the
// Launchpad "New · tasks due" card and the table request (getNewLead) all agree.

export const hasDueNow = (counts) => Boolean(counts) && Object.prototype.hasOwnProperty.call(counts, 'due_now');

/**
 * Headline + chip numbers for the "Tasks due" card, or null while counts are unknown.
 *   new backend: headline = due_now, chips = overdue / due_today as returned.
 *   old backend: headline = due_today (the superset = the true union),
 *                chips    = overdue / (due_today - overdue) so they still sum to it.
 */
export const resolveDueNowCounts = (counts) => {
    if (!counts) return null;
    const overdue = counts.overdue || 0;
    const dueToday = counts.due_today || 0;
    if (hasDueNow(counts)) {
        return { headline: counts.due_now, overdue, dueToday, backendHasDueNow: true };
    }
    return { headline: dueToday, overdue, dueToday: Math.max(0, dueToday - overdue), backendHasDueNow: false };
};

/**
 * The `task_due_bucket` value to actually SEND. The UI keeps 'due_now' as the card's
 * key; on an old backend (counts known, no `due_now`) the request is translated to
 * `due_today` (the superset). Any other bucket, or unknown counts, passes through.
 */
export const resolveRequestBucket = (bucket, counts) => {
    if (bucket === 'due_now' && counts && !hasDueNow(counts)) return 'due_today';
    return bucket;
};

/**
 * Total NEW leads for the practice, derived from the counts (the endpoint returns one
 * bucket per lead, so the buckets partition the NEW population — the same number the
 * unfiltered table paginator shows). `due_now` (new backend) or `due_today` (old
 * backend, where it is the superset including overdue) + upcoming + checklist_complete
 * + no_next_action. null while counts are unknown.
 */
export const resolveNewLeadTotal = (counts) => {
    const due = resolveDueNowCounts(counts);
    if (!due) return null;
    return (due.headline || 0)
        + (counts.upcoming || 0)
        + (counts.checklist_complete || 0)
        + (counts.no_next_action || 0);
};

// ── "No active task" union bucket ───────────────────────────────────────────────
// NEW backend: `new-lead-task-widget-counts` returns a `no_active_task` key (= checklist_complete
// ∪ no_next_action) and `task_due_bucket=no_active_task` filters to that union, so the card's
// headline == its body click == the table rows. OLD backend: no such key/bucket — the body falls
// back to `no_next_action` (the previous behaviour) and the headline stays the chip sum.
export const hasNoActiveTask = (counts) => Boolean(counts) && Object.prototype.hasOwnProperty.call(counts, 'no_active_task');

export const resolveNoActiveTaskBucket = (counts) => (hasNoActiveTask(counts) ? 'no_active_task' : 'no_next_action');

export const resolveNoActiveTaskCount = (counts) => {
    if (!counts) return 0;
    if (hasNoActiveTask(counts)) return counts.no_active_task || 0;
    return (counts.checklist_complete || 0) + (counts.no_next_action || 0);
};
